import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  User, Phone, Mail, Building, MapPin, Briefcase, Calendar, 
  Clock, Shield, Key, Loader2, CheckCircle, Award, Users,
  CreditCard, Heart, Globe, FileText, IdCard, Wallet, 
  GraduationCap, UserCircle, Contact, Building2, Hash, QrCode, Camera, X
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { EmployeeQRCode, EmployeeQRModal } from '../../components/EmployeeQRCode';
import { toast } from 'sonner';

const MyProfile = () => {
  const [employeeData, setEmployeeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showChangePinModal, setShowChangePinModal] = useState(false);
  const [currentPin, setCurrentPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [pinError, setPinError] = useState('');
  const [pinSuccess, setPinSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [lineManager, setLineManager] = useState(null);
  const [showQRModal, setShowQRModal] = useState(false);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const fileInputRef = useRef(null);

  const { user, currentOffice, changePin, pinResetRequired, getAuthHeaders, isOfficeAdmin, isSuperAdmin } = useAuth();
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const canEditProfilePicture = isOfficeAdmin() || isSuperAdmin();

  useEffect(() => {
    fetchEmployeeData();
    fetchLineManager();
  }, []);

  const fetchEmployeeData = async () => {
    try {
      const response = await fetch(`${API_URL}/api/employees/me`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setEmployeeData(data);
      }
    } catch (error) {
      console.error('Error fetching employee data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLineManager = async () => {
    try {
      const response = await fetch(`${API_URL}/api/users/me/line-manager`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setLineManager(data);
      }
    } catch (error) {
      console.error('Error fetching line manager:', error);
    }
  };

  const handleChangePin = async (e) => {
    e.preventDefault();
    setPinError('');
    setPinSuccess('');

    if (newPin !== confirmPin) {
      setPinError('New PINs do not match');
      return;
    }

    if (newPin.length !== 4) {
      setPinError('PIN must be 4 digits');
      return;
    }

    setSubmitting(true);
    const result = await changePin(currentPin, newPin);

    if (result.success) {
      setPinSuccess('PIN changed successfully');
      setCurrentPin('');
      setNewPin('');
      setConfirmPin('');
      setTimeout(() => {
        setShowChangePinModal(false);
        setPinSuccess('');
      }, 2000);
    } else {
      setPinError(result.error);
    }
    setSubmitting(false);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const InfoItem = ({ icon: Icon, label, value, iconColor = 'text-gray-500' }) => (
    <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
      <div className="p-2 rounded-lg bg-white dark:bg-gray-900 shadow-sm">
        <Icon className={`h-4 w-4 ${iconColor}`} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">{label}</p>
        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
          {value || <span className="text-gray-400 italic font-normal">Not provided</span>}
        </p>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="loading-spinner">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Helper function for Title Case
  const toTitleCase = (str) => {
    if (!str) return '';
    return str.toLowerCase().replace(/\b\w/g, char => char.toUpperCase());
  };

  const emp = employeeData;
  
  // Map fields from actual data structure
  const firstName = emp?.first_name || '';
  const lastName = emp?.last_name || '';
  const middleName = emp?.middle_name || '';
  const fullName = toTitleCase(`${firstName} ${lastName}`.trim()) || 'Employee';
  const initials = `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase() || 'E';
  const profilePicture = emp?.profile_picture || null;
  
  // Employment details
  const jobTitle = emp?.employment?.job_title || '';
  const department = emp?.employment?.department || '';
  const employeeId = emp?.employment?.employee_id || emp?.id?.slice(0, 8) || 'N/A';
  const employmentType = emp?.employment?.employment_type || '';
  const employmentStatus = emp?.employment?.employment_status || 'Active';
  const hireDate = emp?.employment?.hire_date;
  const workLocation = emp?.employment?.work_location || '';
  
  // Contact details
  const phone = emp?.contact?.phone || user?.phone || '';
  const email = emp?.contact?.email || '';
  const address = emp?.contact?.address || '';
  const city = emp?.contact?.city || '';
  const postalCode = emp?.contact?.postal_code || '';
  
  // Emergency contact
  const emergencyName = emp?.contact?.emergency_contact_name || '';
  const emergencyPhone = emp?.contact?.emergency_contact_phone || '';
  const emergencyRelationship = emp?.contact?.emergency_contact_relationship || '';
  
  // Personal details
  const dateOfBirth = emp?.date_of_birth;
  const gender = emp?.gender || '';
  const nationality = emp?.nationality || '';
  const nationalId = emp?.national_id || '';
  const taxNumber = emp?.tax_number || '';
  
  // Bank details
  const bankName = emp?.bank_details?.bank_name || '';
  const accountName = emp?.bank_details?.account_name || '';
  const accountNumber = emp?.bank_details?.account_number || '';

  const handleProfilePictureUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size should be less than 5MB');
      return;
    }

    setUploadingPicture(true);

    try {
      // Read file and convert to base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          const headers = getAuthHeaders();
          headers['Content-Type'] = 'application/json';

          const response = await fetch(`${API_URL}/api/employees/${emp.id}/profile-picture`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ image_data: reader.result })
          });

          if (response.ok) {
            const data = await response.json();
            // Update local state
            setEmployeeData(prev => ({ ...prev, profile_picture: data.profile_picture }));
            toast.success(`Profile picture updated! (${data.original_size_kb}KB → ${data.optimized_size_kb}KB)`);
          } else {
            const errorData = await response.json();
            toast.error(errorData.detail || 'Failed to upload profile picture');
          }
        } catch (error) {
          console.error('Error uploading profile picture:', error);
          toast.error('Failed to upload profile picture');
        } finally {
          setUploadingPicture(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error reading file:', error);
      toast.error('Failed to read image file');
      setUploadingPicture(false);
    }
  };

  const handleRemoveProfilePicture = async () => {
    if (!emp?.id) return;

    setUploadingPicture(true);
    try {
      const headers = getAuthHeaders();
      const response = await fetch(`${API_URL}/api/employees/${emp.id}/profile-picture`, {
        method: 'DELETE',
        headers
      });

      if (response.ok) {
        setEmployeeData(prev => ({ ...prev, profile_picture: null }));
        toast.success('Profile picture removed');
      } else {
        toast.error('Failed to remove profile picture');
      }
    } catch (error) {
      console.error('Error removing profile picture:', error);
      toast.error('Failed to remove profile picture');
    } finally {
      setUploadingPicture(false);
    }
  };

  return (
    <div className="space-y-6 max-w-6xl mx-auto" data-testid="my-profile-page">
      {/* PIN Reset Warning */}
      {pinResetRequired && (
        <Alert variant="destructive" data-testid="pin-reset-warning">
          <AlertDescription className="flex items-center justify-between">
            <span>You are using a default PIN. Please change it for security.</span>
            <Button size="sm" variant="outline" onClick={() => setShowChangePinModal(true)}>
              Change PIN
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Profile Header Banner */}
      <Card className="overflow-hidden shadow-sm border-2" style={{ borderColor: '#0779BC' }}>
        <div className="relative p-8 bg-white dark:bg-gray-800">
          <div className="relative flex flex-col md:flex-row items-start gap-6">
            {/* Profile Picture */}
            <div className="relative group">
              {canEditProfilePicture && (
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleProfilePictureUpload}
                  className="hidden"
                  id="profile-picture-upload"
                />
              )}
              {profilePicture ? (
                <div className="relative">
                  <img 
                    src={profilePicture} 
                    alt={fullName}
                    className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-lg"
                  />
                  {/* Overlay buttons on hover - only for admins */}
                  {canEditProfilePicture && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploadingPicture}
                        className="p-2 bg-white rounded-full mr-1 hover:bg-gray-100"
                        title="Change photo"
                      >
                        <Camera className="h-4 w-4 text-gray-700" />
                      </button>
                      <button
                        type="button"
                        onClick={handleRemoveProfilePicture}
                        disabled={uploadingPicture}
                        className="p-2 bg-white rounded-full hover:bg-gray-100"
                        title="Remove photo"
                      >
                        <X className="h-4 w-4 text-red-500" />
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div 
                  className={`w-24 h-24 rounded-full flex items-center justify-center bg-gradient-to-br from-blue-500 to-blue-600 border-4 border-white shadow-lg ${canEditProfilePicture ? 'cursor-pointer hover:opacity-90' : ''} transition-opacity`}
                  onClick={canEditProfilePicture ? () => fileInputRef.current?.click() : undefined}
                  title={canEditProfilePicture ? "Upload photo" : undefined}
                >
                  {uploadingPicture ? (
                    <Loader2 className="h-8 w-8 text-white animate-spin" />
                  ) : (
                    <span className="font-bold text-3xl text-white">{initials}</span>
                  )}
                </div>
              )}
              {canEditProfilePicture && !profilePicture && !uploadingPicture && (
                <div className="absolute -bottom-1 -right-1 p-1.5 bg-blue-500 rounded-full shadow cursor-pointer hover:bg-blue-600" onClick={() => fileInputRef.current?.click()}>
                  <Camera className="h-3 w-3 text-white" />
                </div>
              )}
            </div>

            {/* Name and Job Title */}
            <div className="flex-1">
              <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">{fullName}</h1>
              <p className="text-gray-600 dark:text-gray-400 text-lg mt-1">{jobTitle || 'Employee'}</p>
              <div className="flex flex-wrap items-center gap-2 mt-4">
                {department && (
                  <Badge className="bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600 px-3 py-1">
                    <Building className="h-3 w-3 mr-1.5" />
                    {department}
                  </Badge>
                )}
                <Badge className={`px-3 py-1 ${employmentStatus === 'Active' ? 'bg-green-500' : 'bg-yellow-500'} text-white border-0`}>
                  <CheckCircle className="h-3 w-3 mr-1.5" />
                  {employmentStatus}
                </Badge>
                {employmentType && (
                  <Badge className="bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600 px-3 py-1">
                    {employmentType}
                  </Badge>
                )}
              </div>
            </div>
            
            {/* Employee ID & Actions */}
            <div className="flex flex-col items-end gap-3">
              <div className="text-right bg-gray-50 dark:bg-gray-700 rounded-xl p-4 min-w-[160px] border border-gray-100 dark:border-gray-600">
                <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">Employee ID</p>
                <p className="font-mono text-xl font-bold mt-1 text-gray-900 dark:text-white">{employeeId}</p>
              </div>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setShowQRModal(true)} 
                  className="border-2 border-green-500 text-green-600 hover:bg-green-50"
                  data-testid="show-qr-button"
                >
                  <QrCode className="h-4 w-4 mr-2" />
                  ID Card
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setShowChangePinModal(true)} 
                  className="border-2"
                  style={{ borderColor: '#0779BC', color: '#0779BC' }}
                  data-testid="change-pin-button"
                >
                  <Key className="h-4 w-4 mr-2" />
                  Change PIN
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Tabbed Content */}
      <Tabs defaultValue="personal" className="w-full">
        <TabsList className="grid w-full grid-cols-3 md:grid-cols-6 h-auto p-1 bg-gray-100 dark:bg-gray-800 rounded-xl">
          <TabsTrigger value="personal" className="flex items-center gap-2 py-2.5 data-[state=active]:bg-white dark:data-[state=active]:bg-gray-900 rounded-lg">
            <UserCircle className="h-4 w-4" />
            <span className="hidden sm:inline">Personal</span>
          </TabsTrigger>
          <TabsTrigger value="contact" className="flex items-center gap-2 py-2.5 data-[state=active]:bg-white dark:data-[state=active]:bg-gray-900 rounded-lg">
            <Contact className="h-4 w-4" />
            <span className="hidden sm:inline">Contact</span>
          </TabsTrigger>
          <TabsTrigger value="employment" className="flex items-center gap-2 py-2.5 data-[state=active]:bg-white dark:data-[state=active]:bg-gray-900 rounded-lg">
            <Briefcase className="h-4 w-4" />
            <span className="hidden sm:inline">Employment</span>
          </TabsTrigger>
          <TabsTrigger value="emergency" className="flex items-center gap-2 py-2.5 data-[state=active]:bg-white dark:data-[state=active]:bg-gray-900 rounded-lg">
            <Heart className="h-4 w-4" />
            <span className="hidden sm:inline">Emergency</span>
          </TabsTrigger>
          <TabsTrigger value="financial" className="flex items-center gap-2 py-2.5 data-[state=active]:bg-white dark:data-[state=active]:bg-gray-900 rounded-lg">
            <CreditCard className="h-4 w-4" />
            <span className="hidden sm:inline">Financial</span>
          </TabsTrigger>
          <TabsTrigger value="office" className="flex items-center gap-2 py-2.5 data-[state=active]:bg-white dark:data-[state=active]:bg-gray-900 rounded-lg">
            <Building className="h-4 w-4" />
            <span className="hidden sm:inline">Office</span>
          </TabsTrigger>
        </TabsList>

        {/* Personal Information Tab */}
        <TabsContent value="personal" className="mt-6">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 rounded-lg" style={{ backgroundColor: '#0779BC20' }}>
                  <User className="h-5 w-5" style={{ color: '#0779BC' }} />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Personal Information</h3>
                  <p className="text-sm text-gray-500">Your basic personal details</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <InfoItem icon={User} label="First Name" value={firstName} iconColor="text-blue-600" />
                <InfoItem icon={User} label="Middle Name" value={middleName} iconColor="text-blue-600" />
                <InfoItem icon={User} label="Last Name" value={lastName} iconColor="text-blue-600" />
                <InfoItem icon={Calendar} label="Date of Birth" value={formatDate(dateOfBirth)} iconColor="text-purple-600" />
                <InfoItem icon={Users} label="Gender" value={gender} iconColor="text-pink-600" />
                <InfoItem icon={Globe} label="Nationality" value={nationality} iconColor="text-green-600" />
                <InfoItem icon={IdCard} label="National ID" value={nationalId} iconColor="text-orange-600" />
                <InfoItem icon={Hash} label="Tax Number (TIN)" value={taxNumber} iconColor="text-gray-600" />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Contact Information Tab */}
        <TabsContent value="contact" className="mt-6">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 rounded-lg bg-green-100">
                  <Phone className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Contact Information</h3>
                  <p className="text-sm text-gray-500">Your contact details and address</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <InfoItem icon={Phone} label="Phone Number" value={phone} iconColor="text-green-600" />
                <InfoItem icon={Mail} label="Email Address" value={email} iconColor="text-blue-600" />
                <InfoItem icon={MapPin} label="Address" value={address} iconColor="text-red-500" />
                <InfoItem icon={Building2} label="City" value={city} iconColor="text-purple-600" />
                <InfoItem icon={FileText} label="P.O. Box / Postal Code" value={postalCode} iconColor="text-gray-600" />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Employment Tab */}
        <TabsContent value="employment" className="mt-6">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 rounded-lg bg-purple-100">
                  <Briefcase className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Employment Details</h3>
                  <p className="text-sm text-gray-500">Your job and role information</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <InfoItem icon={IdCard} label="Employee ID" value={employeeId} iconColor="text-indigo-600" />
                <InfoItem icon={Briefcase} label="Job Title" value={jobTitle} iconColor="text-purple-600" />
                <InfoItem icon={Building} label="Department" value={department} iconColor="text-blue-600" />
                <InfoItem icon={Users} label="Employment Type" value={employmentType} iconColor="text-green-600" />
                <InfoItem icon={Shield} label="Employment Status" value={employmentStatus} iconColor="text-emerald-600" />
                <InfoItem icon={Calendar} label="Hire Date" value={formatDate(hireDate)} iconColor="text-orange-600" />
                <InfoItem icon={MapPin} label="Work Location" value={workLocation} iconColor="text-red-500" />
                <InfoItem icon={User} label="Line Manager" value={lineManager?.line_manager_name || emp?.employment?.reporting_manager_name} iconColor="text-indigo-600" />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Emergency Contact Tab */}
        {/* Emergency Contact Tab */}
        <TabsContent value="emergency" className="mt-6">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 rounded-lg bg-red-100">
                  <Heart className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Emergency Contact</h3>
                  <p className="text-sm text-gray-500">Person to contact in case of emergency</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <InfoItem icon={User} label="Contact Name" value={emergencyName} iconColor="text-red-600" />
                <InfoItem icon={Users} label="Relationship" value={emergencyRelationship} iconColor="text-pink-600" />
                <InfoItem icon={Phone} label="Phone Number" value={emergencyPhone} iconColor="text-green-600" />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Financial Tab */}
        <TabsContent value="financial" className="mt-6">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 rounded-lg bg-emerald-100">
                  <CreditCard className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Bank Details</h3>
                  <p className="text-sm text-gray-500">Your bank account information for salary payments</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <InfoItem icon={Building} label="Bank Name" value={bankName} iconColor="text-emerald-600" />
                <InfoItem icon={User} label="Account Name" value={accountName} iconColor="text-blue-600" />
                <InfoItem icon={CreditCard} label="Account Number" value={accountNumber ? `****${accountNumber.slice(-4)}` : null} iconColor="text-purple-600" />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Office Tab */}
        <TabsContent value="office" className="mt-6">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 rounded-lg bg-orange-100">
                  <Building className="h-5 w-5 text-orange-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Office Information</h3>
                  <p className="text-sm text-gray-500">Your office location details</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <InfoItem icon={Building} label="Office Name" value={currentOffice?.name} iconColor="text-orange-600" />
                <InfoItem icon={MapPin} label="Office Location" value={currentOffice?.location} iconColor="text-red-500" />
                <InfoItem icon={Globe} label="Country" value={currentOffice?.country} iconColor="text-green-600" />
                <InfoItem icon={Phone} label="Office Phone" value={currentOffice?.phone} iconColor="text-blue-600" />
                <InfoItem icon={Mail} label="Office Email" value={currentOffice?.email} iconColor="text-indigo-600" />
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Skills Section - only show if skills exist */}
      {emp?.skills && emp.skills.length > 0 && (
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 rounded-lg bg-indigo-100">
                <GraduationCap className="h-5 w-5 text-indigo-600" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">Skills & Qualifications</h3>
                <p className="text-sm text-gray-500">Your professional skills</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {emp.skills.map((skill, idx) => (
                <Badge key={idx} variant="secondary" className="px-4 py-2 text-sm">
                  <Award className="h-3.5 w-3.5 mr-2" />
                  {skill.skill_name}
                  {skill.proficiency_level && (
                    <span className="ml-2 text-xs opacity-70 bg-gray-200 dark:bg-gray-700 px-2 py-0.5 rounded-full">
                      {skill.proficiency_level}
                    </span>
                  )}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Account Status Footer */}
      <Card>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex items-center gap-4 p-4 rounded-xl bg-green-50 dark:bg-green-950/30">
              <div className="p-3 bg-green-100 dark:bg-green-900 rounded-xl">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Account Status</p>
                <p className="font-semibold text-green-600 text-lg">Active</p>
              </div>
            </div>
            <div className="flex items-center gap-4 p-4 rounded-xl bg-blue-50 dark:bg-blue-950/30">
              <div className="p-3 bg-blue-100 dark:bg-blue-900 rounded-xl">
                <Calendar className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Member Since</p>
                <p className="font-semibold text-gray-900 dark:text-white text-lg">
                  {user?.created_at ? formatDate(user.created_at) : 'N/A'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4 p-4 rounded-xl bg-purple-50 dark:bg-purple-950/30">
              <div className="p-3 bg-purple-100 dark:bg-purple-900 rounded-xl">
                <Clock className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Last Login</p>
                <p className="font-semibold text-gray-900 dark:text-white text-lg">
                  {user?.last_login ? new Date(user.last_login).toLocaleDateString() : 'First login'}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Change PIN Modal */}
      <Dialog open={showChangePinModal} onOpenChange={setShowChangePinModal}>
        <DialogContent className="sm:max-w-md" data-testid="change-pin-modal">
          <DialogHeader>
            <DialogTitle>Change PIN</DialogTitle>
            <DialogDescription>Enter your current PIN and choose a new one</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleChangePin} className="space-y-4">
            {pinError && (
              <Alert variant="destructive">
                <AlertDescription>{pinError}</AlertDescription>
              </Alert>
            )}
            {pinSuccess && (
              <Alert>
                <AlertDescription className="text-green-600">{pinSuccess}</AlertDescription>
              </Alert>
            )}
            <div className="space-y-2">
              <Label htmlFor="current-pin">Current PIN</Label>
              <Input
                id="current-pin"
                type="password"
                value={currentPin}
                onChange={(e) => setCurrentPin(e.target.value)}
                placeholder="Enter current PIN"
                maxLength={4}
                required
                data-testid="current-pin-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-pin">New PIN</Label>
              <Input
                id="new-pin"
                type="password"
                value={newPin}
                onChange={(e) => setNewPin(e.target.value)}
                placeholder="Enter new 4-digit PIN"
                maxLength={4}
                required
                data-testid="new-pin-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-pin">Confirm New PIN</Label>
              <Input
                id="confirm-pin"
                type="password"
                value={confirmPin}
                onChange={(e) => setConfirmPin(e.target.value)}
                placeholder="Confirm new PIN"
                maxLength={4}
                required
                data-testid="confirm-pin-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowChangePinModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} data-testid="submit-change-pin-button">
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Changing...
                  </>
                ) : (
                  'Change PIN'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* QR Code Modal */}
      <EmployeeQRModal 
        open={showQRModal} 
        onClose={() => setShowQRModal(false)} 
        employeeId="me"
        employeeName={fullName}
      />
    </div>
  );
};

export default MyProfile;
